// Reads and writes of MRP and its planned orders (spec 03 §5 rows 38 to 49),
// all through the generated client. Every queryFn passes its signal and every
// write carries a fresh Idempotency-Key.

import { useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { components, paths } from "@assessly/api-client";
import { client } from "../../../../lib/api";
import { refreshQuery, shouldRetryRead, type ReadContext } from "../../../../lib/query";
import { newIdempotencyKey, unwrap } from "../../../../lib/request";

type S = components["schemas"];
export type MrpRun = S["MrpRun"];
export type MrpRunStats = S["MrpRunStats"];
export type MrpRunList = S["MrpRunListResponse"];
export type MrpRunAccepted = S["MrpRunAccepted"];
export type MrpGroupPassAccepted = S["MrpGroupPassAccepted"];
export type MrpRunStatus = MrpRun["status"];
export type MrpRunTrigger = MrpRun["trigger"];
export type MrpRunError = NonNullable<MrpRun["error"]>;
export type PlannedOrder = S["PlannedOrder"];
export type PlannedOrderList = S["PlannedOrderListResponse"];
export type PlannedOrderSummary = S["PlannedOrderSummary"];
export type PlannedOrderDetail = S["PlannedOrderDetail"];
export type PlannedOrderExplanation = S["PlannedOrderExplanation"];
export type ExplanationDemandItem = S["ExplanationDemandItem"];
export type ExplanationScrapStep = S["ExplanationScrapStep"];
export type ExplanationOffset = S["ExplanationOffset"];
export type ExplanationLabel = S["ExplanationLabel"];
export type PegEdge = S["PegEdge"];
export type PegReducedNode = S["PegReducedNode"];
export type PlannedOrderKind = PlannedOrder["kind"];
export type PlannedOrderStatus = PlannedOrder["status"];
export type ActionMessageKind = NonNullable<PlannedOrder["action_message"]>;
export type ExpectedActionMessage = NonNullable<S["ExpectedActionMessage"]>;
export type PlannedOrderBulkResult = S["PlannedOrderBulkResult"];
export type PlannedOrderConvertResult = S["PlannedOrderConvertResult"];
export type PlannedOrderConvertedKind = NonNullable<PlannedOrder["converted_kind"]>;
type PlannedOrderQuery = NonNullable<paths["/v1/planned-orders"]["get"]["parameters"]["query"]>;

export const PLANNED_ORDER_KINDS: readonly PlannedOrderKind[] = [
  "manufacture",
  "purchase",
  "transfer",
];
export const PLANNED_ORDER_STATUSES: readonly PlannedOrderStatus[] = [
  "proposed",
  "firmed",
  "converted",
  "cancelled",
];
export const ACTION_MESSAGE_KINDS: readonly ActionMessageKind[] = [
  "reschedule_in",
  "reschedule_out",
  "increase_qty",
  "decrease_qty",
  "cancel",
  "expedite",
];
export const MRP_RUN_STATUSES: readonly MrpRunStatus[] = [
  "queued",
  "running",
  "succeeded",
  "failed",
];

/** The most ids one firm, one bulk accept or one cancel fan-out carries (contracts `PLANNED_ORDER_BULK_MAX`). */
export const PLANNED_ORDER_BULK_MAX = 200;
/** The longest cancel or dismiss reason (contracts `PLANNED_ORDER_REASON_MAX`). */
export const PLANNED_ORDER_REASON_MAX = 500;
/** The list's page size. */
export const PLANNED_ORDER_PAGE = 50;
/** How often a read re-asks while a run is queued or running. */
export const MRP_BUSY_POLL_MS = 5_000;
/** How often the workbench re-asks otherwise, so a run the worker started shows unasked. */
export const MRP_IDLE_POLL_MS = 60_000;

/** The list filters of §5 row 42, as the screen holds them. */
export interface PlannedOrderFilters {
  readonly kind?: PlannedOrderKind | undefined;
  readonly status?: PlannedOrderStatus | undefined;
  /** `true`, `false` or one message kind. */
  readonly message?: "true" | "false" | ActionMessageKind | undefined;
  readonly from?: string | undefined;
  readonly to?: string | undefined;
  readonly product?: string | undefined;
  readonly planner?: string | undefined;
  readonly run?: string | undefined;
  readonly line?: string | undefined;
  readonly entry?: string | undefined;
}

export const mrpKeys = {
  all: (facilityId: string) => ["planning", "mrp", facilityId] as const,
  runs: (facilityId: string, cursor: string | undefined) =>
    ["planning", "mrp", facilityId, "runs", cursor ?? null] as const,
  run: (id: string) => ["planning", "mrp-run", id] as const,
  summary: (facilityId: string) => ["planning", "mrp", facilityId, "summary"] as const,
  orders: (facilityId: string, filters: PlannedOrderFilters, cursor: string | undefined) =>
    ["planning", "mrp", facilityId, "orders", filters, cursor ?? null] as const,
  order: (id: string) => ["planning", "planned-order", id] as const,
};

function runsBusy(list: MrpRunList | undefined): boolean {
  return list?.items.some((r) => r.status === "queued" || r.status === "running") ?? false;
}

/** One page of the facility's run history, with the failure count and the nightly watermark. */
export function useMrpRuns(facilityId: string, cursor: string | undefined, enabled = true) {
  return useQuery({
    queryKey: mrpKeys.runs(facilityId, cursor),
    enabled: enabled && facilityId.length > 0,
    retry: shouldRetryRead,
    refetchInterval: (query) => (runsBusy(query.state.data) ? MRP_BUSY_POLL_MS : MRP_IDLE_POLL_MS),
    queryFn: async ({ signal }: ReadContext): Promise<MrpRunList> =>
      unwrap(
        await client.GET("/v1/mrp/runs", {
          signal,
          params: {
            query: {
              facility_id: facilityId,
              limit: 20,
              ...(cursor !== undefined ? { cursor } : {}),
            },
          },
        }),
        "MRP runs",
      ),
  });
}

/** One run; re-read while it is queued or running. */
export function useMrpRun(id: string | undefined) {
  return useQuery({
    queryKey: mrpKeys.run(id ?? ""),
    enabled: id !== undefined && id.length > 0,
    retry: shouldRetryRead,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === "queued" || status === "running" ? MRP_BUSY_POLL_MS : false;
    },
    queryFn: async ({ signal }: ReadContext): Promise<MrpRun> =>
      unwrap(
        await client.GET("/v1/mrp/runs/{id}", { signal, params: { path: { id: id ?? "" } } }),
        "MRP run",
      ),
  });
}

/** Counts by status and kind, and messages by kind (§5 row 43). */
export function usePlannedOrderSummary(facilityId: string, enabled = true, poll = true) {
  return useQuery({
    queryKey: mrpKeys.summary(facilityId),
    enabled: enabled && facilityId.length > 0,
    retry: shouldRetryRead,
    // `poll: false` for a reader that must not move after it renders (the
    // office home, design-system §5).
    refetchInterval: poll ? MRP_IDLE_POLL_MS : false,
    queryFn: async ({ signal }) =>
      unwrap(
        await client.GET("/v1/planned-orders/summary", {
          signal,
          params: { query: { facility_id: facilityId } },
        }),
        "planned order summary",
      ),
  });
}

/** The query §5 row 42 takes for the screen's filters. */
export function plannedOrderQuery(
  facilityId: string,
  filters: PlannedOrderFilters,
  cursor: string | undefined,
): PlannedOrderQuery {
  const query: PlannedOrderQuery = {
    facility_id: facilityId,
    limit: PLANNED_ORDER_PAGE,
    sort: "due_date",
  };
  if (filters.kind !== undefined) query.kind = filters.kind;
  if (filters.status !== undefined) query.status = filters.status;
  if (filters.message !== undefined) query.action_message = filters.message;
  if (filters.from !== undefined) query.from = filters.from;
  if (filters.to !== undefined) query.to = filters.to;
  if (filters.product !== undefined) query.product_id = filters.product;
  if (filters.planner !== undefined) query.planner_code = filters.planner;
  if (filters.run !== undefined) query.mrp_run_id = filters.run;
  if (filters.line !== undefined) query.pegged_to_sales_order_line_id = filters.line;
  if (filters.entry !== undefined) query.pegged_to_demand_entry_id = filters.entry;
  if (cursor !== undefined) query.cursor = cursor;
  return query;
}

/** One page of the facility's planned orders under the filters. */
export function usePlannedOrders(
  facilityId: string,
  filters: PlannedOrderFilters,
  cursor: string | undefined,
) {
  return useQuery({
    queryKey: mrpKeys.orders(facilityId, filters, cursor),
    enabled: facilityId.length > 0,
    retry: shouldRetryRead,
    queryFn: async ({ signal }: ReadContext): Promise<PlannedOrderList> =>
      unwrap(
        await client.GET("/v1/planned-orders", {
          signal,
          params: { query: plannedOrderQuery(facilityId, filters, cursor) },
        }),
        "planned orders",
      ),
  });
}

/** Every id the filters select, at most `max`, read page by page (§4.5's filter-to-selection). */
export async function readPlannedOrderIds(
  facilityId: string,
  filters: PlannedOrderFilters,
  max: number = PLANNED_ORDER_BULK_MAX,
): Promise<{ rows: PlannedOrder[]; more: boolean }> {
  const rows: PlannedOrder[] = [];
  let cursor: string | undefined;
  for (;;) {
    const page = unwrap(
      await client.GET("/v1/planned-orders", {
        params: { query: plannedOrderQuery(facilityId, filters, cursor) },
      }),
      "planned orders",
    );
    for (const row of page.items) {
      if (rows.length >= max) return { rows, more: true };
      rows.push(row);
    }
    if (page.next_cursor === null) return { rows, more: false };
    cursor = page.next_cursor;
  }
}

/** One order with its explanation, labels and pegs (§5 row 44). */
export function usePlannedOrder(id: string | undefined) {
  return useQuery({
    queryKey: mrpKeys.order(id ?? ""),
    enabled: id !== undefined && id.length > 0,
    retry: shouldRetryRead,
    queryFn: async ({ signal }: ReadContext): Promise<PlannedOrderDetail> =>
      unwrap(
        await client.GET("/v1/planned-orders/{id}", {
          signal,
          params: { path: { id: id ?? "" } },
        }),
        "planned order",
      ),
  });
}

// ---- writes --------------------------------------------------------------------

/** `POST /v1/mrp/runs`: the facility's queued run, joined or inserted. */
export async function requestMrpRun(facilityId: string): Promise<MrpRunAccepted> {
  return unwrap(
    await client.POST("/v1/mrp/runs", {
      params: { header: { "Idempotency-Key": newIdempotencyKey() } },
      body: { facility_id: facilityId },
    }),
    "MRP run",
  );
}

/** `POST /v1/mrp/runs/group-wide`: the manual pass's order and its skips. */
export async function requestMrpGroupPass(): Promise<MrpGroupPassAccepted> {
  return unwrap(
    await client.POST("/v1/mrp/runs/group-wide", {
      params: { header: { "Idempotency-Key": newIdempotencyKey() } },
      body: {},
    }),
    "MRP group pass",
  );
}

export async function firmPlannedOrders(ids: readonly string[]) {
  return unwrap(
    await client.POST("/v1/planned-orders/firm", {
      params: { header: { "Idempotency-Key": newIdempotencyKey() } },
      body: { ids: [...ids] },
    }),
    "firm planned orders",
  );
}

/**
 * `POST /v1/planned-orders/convert` (§3.4, §5 row 50): each id converts in its
 * own transaction and the answer is per id, in the order sent.
 */
export async function convertPlannedOrders(ids: readonly string[]) {
  return unwrap(
    await client.POST("/v1/planned-orders/convert", {
      params: { header: { "Idempotency-Key": newIdempotencyKey() } },
      body: { ids: [...ids] },
    }),
    "convert planned orders",
  );
}

export async function cancelPlannedOrder(id: string, reason: string) {
  return unwrap(
    await client.POST("/v1/planned-orders/{id}/cancel", {
      params: { path: { id }, header: { "Idempotency-Key": newIdempotencyKey() } },
      body: { reason },
    }),
    "cancel planned order",
  );
}

export async function acceptActionMessage(id: string, expected: ExpectedActionMessage) {
  return unwrap(
    await client.POST("/v1/planned-orders/{id}/action-message/accept", {
      params: { path: { id }, header: { "Idempotency-Key": newIdempotencyKey() } },
      body: { expected_message: expected },
    }),
    "accept action message",
  );
}

export async function dismissActionMessage(
  id: string,
  expected: ExpectedActionMessage,
  reason: string,
) {
  return unwrap(
    await client.POST("/v1/planned-orders/{id}/action-message/dismiss", {
      params: { path: { id }, header: { "Idempotency-Key": newIdempotencyKey() } },
      body: { expected_message: expected, reason },
    }),
    "dismiss action message",
  );
}

export async function bulkAcceptActionMessages(
  items: readonly { id: string; expected_message: ExpectedActionMessage }[],
) {
  return unwrap(
    await client.POST("/v1/planned-orders/action-messages/accept", {
      params: { header: { "Idempotency-Key": newIdempotencyKey() } },
      body: items.map((i) => ({ id: i.id, expected_message: i.expected_message })),
    }),
    "accept action messages",
  );
}

/** Re-reads every MRP read of a facility, and one order's detail, after a write. */
export function useMrpRefresh() {
  const qc = useQueryClient();
  return useCallback(
    async (facilityId: string, orderIds: readonly string[] = []) => {
      await Promise.all([
        refreshQuery(qc, mrpKeys.all(facilityId)),
        ...orderIds.map((id) => refreshQuery(qc, mrpKeys.order(id))),
      ]);
    },
    [qc],
  );
}
